import React, { useState, useEffect } from "react";
import { Scanner } from "@yudiel/react-qr-scanner";
import { API_BASE_URL } from "../../config/baseUrls";

const EventTicketScanner = () => {
  const [scanning, setScanning] = useState(true);
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [cameraError, setCameraError] = useState(false);
  const [activeMode, setActiveMode] = useState("TICKET");
  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    onConfirm: null,
  });

  const Modal = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    type = "info",
  }) => {
    if (!isOpen) return null;

    const isConfirm = type === "confirm";
    const isWarning = type === "warning";

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
          <div
            className={`p-4 sm:p-6 border-b ${
              isWarning ? "bg-red-50" : "bg-blue-50"
            }`}
          >
            <div className="flex items-center space-x-3">
              <div className="text-3xl">
                {isWarning ? "⚠️" : isConfirm ? "❓" : "✅"}
              </div>
              <h3
                className={`text-lg sm:text-xl font-bold ${
                  isWarning ? "text-red-800" : "text-blue-800"
                }`}
              >
                {title}
              </h3>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <p className="text-sm sm:text-base text-gray-700 whitespace-pre-line">
              {message}
            </p>
          </div>

          <div
            className={`p-4 sm:p-6 border-t flex ${
              isConfirm ? "justify-between" : "justify-end"
            } gap-3`}
          >
            {isConfirm && (
              <button
                onClick={onClose}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium text-sm sm:text-base"
              >
                Cancel
              </button>
            )}
            <button
              onClick={() => {
                if (onConfirm) onConfirm();
                onClose();
              }}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium text-sm sm:text-base ${
                isWarning
                  ? "bg-red-600 hover:bg-red-700 text-white"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
            >
              {isConfirm ? "Confirm" : "OK"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const showAlert = (title, message, type = "info") => {
    setModal({
      isOpen: true,
      title,
      message,
      type,
      onConfirm: null,
    });
  };

  const showConfirm = (title, message, onConfirm) => {
    setModal({
      isOpen: true,
      title,
      message,
      type: "confirm",
      onConfirm,
    });
  };

  const closeModal = () => {
    setModal({ ...modal, isOpen: false });
  };

  useEffect(() => {
    const token = localStorage.getItem("eventScannerToken");
    const role = localStorage.getItem("scannerUserRole");
    if (token && role === "mod") {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    const connectionCheck = setInterval(() => {
      setIsOnline(navigator.onLine);
    }, 5000);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(connectionCheck);
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/content/signin`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: credentials.email,
            password: credentials.password,
          }),
        },
      );

      const data = await response.json();

      if (data.token && data.user) {
        if (data.user.role !== "MODERATOR") {
          setLoginError(
            "Access Denied: Only moderators can access the event scanner.",
          );
          setLoading(false);
          return;
        }

        localStorage.setItem("eventScannerToken", data.token);
        localStorage.setItem("scannerUserId", data.user.cuid);
        localStorage.setItem(
          "scannerUserName",
          data.user.nickname || data.user.email,
        );
        localStorage.setItem("scannerUserRole", data.user.role);
        setIsAuthenticated(true);
      } else {
        setLoginError(
          data.message || "Login failed. Please check your credentials.",
        );
      }
    } catch (err) {
      console.error("Login error:", err);
      setLoginError("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("eventScannerToken");
    localStorage.removeItem("scannerUserId");
    localStorage.removeItem("scannerUserName");
    localStorage.removeItem("scannerUserRole");
    setIsAuthenticated(false);
    setScanResult(null);
    setScanning(true);
    setCredentials({ email: "", password: "" });
  };
  const onScanSuccess = async (decodedText) => {
    console.log("=== QR CODE SCANNED ===");

    let cleanData;
    try {
      cleanData = JSON.parse(decodedText);
      if (typeof cleanData === "string") {
        cleanData = JSON.parse(cleanData);
      }
    } catch (parseError) {
      console.error("Failed to parse QR data:", parseError);
      setError("Invalid QR code format");
      setTimeout(() => resetScanner(), 3000);
      return;
    }

    const stringifiedQRData = JSON.stringify(cleanData);

    const requestPayload = {
      qrData: stringifiedQRData,
      location: activeMode === "WORKSHOP" ? "Workshop Venue" : "Event Venue",
      scannedBy: localStorage.getItem("scannerUserId"),
    };

    setScanning(false);
    setLoading(true);
    setError(null);

    const endpoint =
      activeMode === "WORKSHOP"
        ? "/tickets/addon-events/scan-pass"
        : "/tickets/scan";

    try {
      const response = await fetch(
        `${API_BASE_URL}${endpoint}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem(
              "eventScannerToken",
            )}`,
          },
          body: JSON.stringify(requestPayload),
        },
      );

      const data = await response.json();
      if (data.success) {
        setScanResult(data.data);
      } else {
        setError(data.error || "Validation failed");
        setTimeout(() => resetScanner(), 3000);
      }
    } catch (err) {
      setError("Failed to process scan");
      setTimeout(() => resetScanner(), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsUsed = async (scanType = "MAIN") => {
    setLoading(true);

    let url;
    if (activeMode === "WORKSHOP") {
      // Workshop passes use /addon-events/pass/use/:passId
      url = `${API_BASE_URL}/tickets/addon-events/pass/use/${scanResult.passId}`;
    } else {
      // Standard tickets use /:ticketId/use/:scanType
      url = `${API_BASE_URL}/tickets/${scanResult.ticketId}/use/${scanType}`;
    }
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("eventScannerToken")}`,
        },
        body: JSON.stringify({
          location: "Event Venue",
          staffId: localStorage.getItem("scannerUserId"),
        }),
      });

      const data = await response.json();

      if (data.success) {
        showAlert(
          "Success",
          `${scanType.replace("_", " ")} Entry marked successfully`,
          "info",
        );
        setTimeout(() => resetScanner(), 2000);
      } else {
        showAlert("Error", `Failed: ${data.error}`, "warning");
      }
    } catch (err) {
      console.error("Mark as used error:", err);
      showAlert("Error", "Failed to mark entry", "warning");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsCancelled = async () => {
    showConfirm(
      "Cancel Ticket",
      "Are you sure you want to CANCEL this ticket?\n\nThis action:\n• Cannot be undone\n• Will permanently invalidate the ticket\n• Customer will need to purchase a new ticket",
      async () => {
        setLoading(true);
        try {
          const response = await fetch(
            `${API_BASE_URL}/tickets/${scanResult.ticketId}`,
            {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem(
                  "eventScannerToken",
                )}`,
              },
              body: JSON.stringify({
                userId: "staff-override",
              }),
            },
          );

          const data = await response.json();

          if (data.success) {
            showAlert(
              "Success",
              "Ticket marked as CANCELLED successfully",
              "info",
            );
            setTimeout(() => resetScanner(), 2000);
          } else {
            showAlert("Error", `Failed: ${data.error}`, "warning");
          }
        } catch (err) {
          console.error("Cancel error:", err);
          showAlert("Error", "Failed to cancel ticket", "warning");
        } finally {
          setLoading(false);
        }
      },
    );
  };

  const resetScanner = () => {
    setScanResult(null);
    setError(null);
    setScanning(true);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-4 sm:p-6">
        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-md">
          <div className="text-center mb-6 sm:mb-8">
            <div className="text-5xl sm:text-6xl mb-3 sm:mb-4">🎫</div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Event Scanner
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mt-2">
              Moderator Access Only
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-3 sm:space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Email
              </label>
              <input
                type="email"
                value={credentials.email}
                onChange={(e) =>
                  setCredentials({ ...credentials, email: e.target.value })
                }
                className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="moderator@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Password
              </label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) =>
                  setCredentials({ ...credentials, password: e.target.value })
                }
                className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your password"
                required
              />
            </div>

            {loginError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2 sm:p-3">
                <p className="text-xs sm:text-sm text-red-700 text-center">
                  {loginError}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 sm:py-3 text-base sm:text-lg bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {loading ? "Logging in..." : "Login as Moderator"}
            </button>
          </form>

          <div className="mt-4 sm:mt-6 text-center">
            <p className="text-xs text-gray-500">
              Only users with moderator role can access this scanner
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Internet Connection Warning Modal */}
      {!isOnline && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-md w-full text-center">
            <div className="text-6xl sm:text-7xl mb-4 animate-pulse">⚠️</div>
            <h2 className="text-2xl sm:text-3xl font-bold text-red-600 mb-3">
              WARNING
            </h2>
            <p className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
              Stable Internet Connection Required!
            </p>
            <p className="text-sm sm:text-base text-gray-600 mb-4">
              The scanner requires an active internet connection to verify
              tickets.
            </p>
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-3 sm:p-4">
              <p className="text-sm text-red-700 font-medium">
                Please check your connection and wait for it to stabilize.
              </p>
            </div>
            <div className="mt-4 flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
              <span className="text-sm text-gray-500">
                Checking connection...
              </span>
            </div>
          </div>
        </div>
      )}
      {/* Camera Not Found Modal */}
      {cameraError && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-md w-full text-center">
            <div className="text-6xl sm:text-7xl mb-4">📷</div>
            <h2 className="text-2xl sm:text-3xl font-bold text-red-600 mb-3">
              Camera Not Found
            </h2>
            <p className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
              This device is not supported
            </p>
            <p className="text-sm sm:text-base text-gray-600 mb-4">
              No camera detected on this device. Please use a device with a
              working camera to scan tickets.
            </p>
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-3 sm:p-4 mb-4">
              <p className="text-sm text-red-700 font-medium">
                Make sure you've granted camera permissions and that your camera
                is functioning properly.
              </p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">
                Event Ticket Scanner
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                {localStorage.getItem("scannerUserName")}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="ml-4 px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 whitespace-nowrap"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        {/* Mode Switcher Toggle */}
        {!scanResult && !loading && (
          <div className="flex bg-white rounded-xl p-1 shadow-sm border border-gray-200 mb-6">
            <button
              onClick={() => {
                setActiveMode("TICKET");
                resetScanner();
              }}
              className={`flex-1 flex items-center justify-center space-x-2 py-3 rounded-lg font-bold transition-all ${
                activeMode === "TICKET"
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              <span className="text-xl">🎫</span>
              <span className="text-sm sm:text-base">Event Tickets</span>
            </button>
            <button
              onClick={() => {
                setActiveMode("WORKSHOP");
                resetScanner();
              }}
              className={`flex-1 flex items-center justify-center space-x-2 py-3 rounded-lg font-bold transition-all ${
                activeMode === "WORKSHOP"
                  ? "bg-amber-600 text-white shadow-md"
                  : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              <span className="text-xl">🛠️</span>
              <span className="text-sm sm:text-base">Workshop Passes</span>
            </button>
          </div>
        )}
        {/* Scanner View */}
        {scanning && !scanResult && (
          <div className="space-y-4">
            {/* Status Box */}
            <div className="bg-white rounded-lg shadow-sm border-2 border-blue-500 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="text-2xl">📱</div>
                  <div>
                    <h2 className="text-base sm:text-lg font-bold text-gray-900">
                      Ticket Scanner
                    </h2>
                    <p className="text-xs sm:text-sm text-gray-600">
                      Ready to scan
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 bg-green-100 rounded-full px-3 py-1.5">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs font-semibold text-green-700">
                    Active
                  </span>
                </div>
              </div>
            </div>

            {/* Scanner */}
            <div
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
              style={{
                position: "relative",
                width: "100%",
                paddingBottom: "100%",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
              >
                <Scanner
                  onScan={(result) => {
                    if (result && result[0]) {
                      onScanSuccess(result[0].rawValue);
                    }
                  }}
                  onError={(error) => {
                    console.error("Scanner error:", error);
                    if (
                      error?.name === "NotFoundError" ||
                      error?.message?.includes("not found")
                    ) {
                      setCameraError(true);
                    }
                  }}
                  constraints={{
                    facingMode: "environment",
                    focusMode: "continuous",
                  }}
                  components={{
                    audio: false,
                    finder: true,
                  }}
                  styles={{
                    container: {
                      width: "100%",
                      height: "100%",
                    },
                    video: {
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    },
                  }}
                />
              </div>
            </div>

            {/* Tips */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-start space-x-3">
                <span className="text-xl flex-shrink-0">💡</span>
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-gray-900 mb-2">
                    Quick Tips
                  </h3>
                  <ul className="text-xs text-gray-700 space-y-1.5">
                    <li>• Ensure good lighting and hold device steady</li>
                    <li>• Center the QR code within the frame</li>
                    <li>• Scanning happens automatically when detected</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-4 border-blue-600 mx-auto mb-3 sm:mb-4"></div>
            <p className="text-sm sm:text-base text-gray-600">
              Processing ticket...
            </p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-6 text-center">
            <div className="text-5xl sm:text-6xl mb-3 sm:mb-4">❌</div>
            <h2 className="text-lg sm:text-xl font-bold text-red-800 mb-2">
              Scan Failed
            </h2>
            <p className="text-sm sm:text-base text-red-700">{error}</p>
            <p className="text-xs sm:text-sm text-red-600 mt-2">
              Returning to scanner...
            </p>
          </div>
        )}
        {scanResult && activeMode === "WORKSHOP" && (
          <div className="bg-white rounded-xl shadow-lg border-t-8 border-amber-500 overflow-hidden">
            <div className="p-6">
              {/* 🛠️ WORKSHOP DETAILS SECTION */}
              <div className="mb-6">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                      Workshop/Add-On
                    </h4>
                    <h3 className="text-xl font-black text-gray-900">
                      {scanResult.passTitle}
                    </h3>
                  </div>
                  <span className="bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-1 rounded">
                    {scanResult.passNumber}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{scanResult.eventName}</p>
              </div>

              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100 mb-6 flex items-center space-x-4">
                <div className="bg-amber-100 p-2 rounded-lg text-2xl">⏰</div>
                <div className="flex-1">
                  <p className="text-[10px] font-black text-amber-800 uppercase tracking-widest mb-1">
                    Scheduled Workshop Session
                  </p>
                  <p className="text-lg font-black text-amber-900 leading-tight">
                    {scanResult.timeslot?.start
                      ? new Date(scanResult.timeslot.start).toLocaleTimeString(
                          [],
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          },
                        )
                      : "N/A"}
                    <span className="mx-2 text-amber-400">→</span>
                    {scanResult.timeslot?.end
                      ? new Date(scanResult.timeslot.end).toLocaleTimeString(
                          [],
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          },
                        )
                      : "N/A"}
                  </p>
                  <p className="text-xs font-bold text-amber-600 mt-1">
                    {scanResult.timeslot?.start
                      ? new Date(scanResult.timeslot.start).toDateString()
                      : ""}
                  </p>
                </div>
              </div>

              {/* 👤 REFINED ATTENDEE INFO */}
              <div className="border-t border-gray-100 pt-6">
                <h4 className="text-sm font-bold text-gray-700 mb-4 flex items-center">
                  <span className="mr-2">👤</span> Attendee Profile
                </h4>
                <div className="flex items-center space-x-4 bg-gray-50 p-3 rounded-lg">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-xl font-bold text-blue-600">
                    {scanResult.attendeeInfo?.name?.charAt(0) || "G"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-bold text-gray-900 truncate">
                      {scanResult.attendeeInfo?.name}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {scanResult.attendeeInfo?.email}
                    </p>
                  </div>
                </div>
              </div>

              {/* 💰 PRICING/PAYMENT STATUS */}
              <div className="mt-4 flex justify-between items-center bg-gray-50 p-3 rounded-lg border border-gray-100">
                <div>
                  <p className="text-[10px] text-gray-500 font-bold uppercase">
                    Payment Received
                  </p>
                  <p className="text-sm font-black text-gray-900">
                    ₹
                    {scanResult.pricing?.totalAmount ||
                      scanResult.totalAmount ||
                      "0.00"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-gray-500 font-bold uppercase">
                    Status
                  </p>
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full font-black uppercase ${
                      scanResult.passStatus === "CONFIRMED"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {scanResult.passStatus}
                  </span>
                </div>
              </div>
            </div>

            {/* ACTION BUTTON */}
            {scanResult.isValid && scanResult.passStatus === "CONFIRMED" && (
              <div className="p-4 bg-gray-50 border-t">
                <button
                  onClick={() => handleMarkAsUsed("WORKSHOP")}
                  disabled={loading}
                  className="w-full py-4 bg-amber-600 text-white rounded-xl font-black text-xl shadow-xl hover:bg-amber-700 transition-transform active:scale-95"
                >
                  ✅ Complete Workshop Check-in
                </button>
              </div>
            )}
          </div>
        )}

        {/* Scan Result View */}
        {scanResult && !loading && activeMode === "TICKET" && (
          <div className="space-y-3 sm:space-y-4">
            <div
              className={`rounded-lg p-4 sm:p-6 text-center ${
                scanResult.isValid &&
                ["RESERVED", "CONFIRMED", "USED"].includes(
                  scanResult.ticketStatus,
                )
                  ? "bg-green-50 border-2 border-green-500"
                  : "bg-red-50 border-2 border-red-500"
              }`}
            >
              <div className="text-5xl sm:text-6xl mb-3 sm:mb-4">
                {scanResult.isValid &&
                ["RESERVED", "CONFIRMED", "USED"].includes(
                  scanResult.ticketStatus,
                )
                  ? "✅"
                  : "❌"}
              </div>
              <h2
                className={`text-xl sm:text-2xl font-bold mb-2 ${
                  scanResult.isValid &&
                  ["RESERVED", "CONFIRMED", "USED"].includes(
                    scanResult.ticketStatus,
                  )
                    ? "text-green-800"
                    : "text-red-800"
                }`}
              >
                {scanResult.message}
              </h2>
              <p
                className={`text-base sm:text-lg font-semibold ${
                  scanResult.isValid &&
                  ["RESERVED", "CONFIRMED", "USED"].includes(
                    scanResult.ticketStatus,
                  )
                    ? "text-green-700"
                    : "text-red-700"
                }`}
              >
                Status: {scanResult.ticketStatus}
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">
                Ticket Details
              </h3>

              <div className="mb-4 sm:mb-6">
                <h4 className="text-sm sm:text-base font-semibold text-gray-700 mb-2">
                  Payment Information
                </h4>
                <div className="bg-gray-50 rounded p-3 sm:p-4 space-y-1.5 sm:space-y-2">
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span>Coupon Used:</span>
                    <span className="font-medium">
                      {scanResult.pricing.couponCode}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span>Ticket Price:</span>
                    <span className="font-medium">
                      ₹
                      {scanResult.pricing.originalPrice -
                        scanResult.pricing.couponDiscount}
                      {""}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span>Platform Fee:</span>
                    <span className="font-medium">
                      ₹{scanResult.pricing.fees}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span>Taxes:</span>
                    <span className="font-medium">
                      ₹{scanResult.pricing.taxes}
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-1.5 sm:pt-2 font-bold text-base sm:text-lg">
                    <span>Total Amount:</span>
                    <span className="text-green-600">
                      ₹{scanResult.pricing.total}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mb-4 sm:mb-6 bg-purple-50 border border-purple-100 rounded-lg p-4">
                <h4 className="text-sm sm:text-base font-bold text-purple-900 mb-3 flex items-center">
                  <span className="mr-2">🎭</span> Activity & Slot
                </h4>

                <div className="space-y-3">
                  {/* Ticket Type Badge */}
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-purple-700 font-semibold uppercase tracking-wide">
                      Ticket Type
                    </span>
                    <span className="bg-purple-200 text-purple-800 text-xs font-bold px-2 py-1 rounded">
                      {scanResult.ticketType}
                    </span>
                  </div>

                  {/* Timeslot Card */}
                  {scanResult.activityDetails?.timeslot && (
                    <div className="bg-white rounded-md p-3 border border-purple-100 shadow-sm">
                      <p className="text-[10px] text-purple-400 font-bold uppercase tracking-wider mb-1">
                        Assigned Time
                      </p>
                      <div className="flex items-baseline space-x-2">
                        <p className="text-lg font-black text-purple-900">
                          {new Date(
                            scanResult.activityDetails.timeslot.start,
                          ).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                        <span className="text-purple-300">→</span>
                        <p className="text-lg font-black text-purple-900">
                          {new Date(
                            scanResult.activityDetails.timeslot.end,
                          ).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                      <p className="text-xs font-medium text-purple-600 mt-1 border-t border-purple-50 pt-1">
                        📅{" "}
                        {new Date(
                          scanResult.activityDetails.timeslot.start,
                        ).toLocaleDateString(undefined, {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">
                  Ticket Information
                </h3>

                {/* NEW: Entry Status Section */}
                <div className="mb-4 sm:mb-6">
                  <h4 className="text-sm sm:text-base font-semibold text-gray-700 mb-2">
                    Entry Status
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div
                      className={`p-2 rounded border text-center ${
                        scanResult.isMainEntryScanned
                          ? "bg-green-50 border-green-200"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <p className="text-[10px] uppercase text-gray-500 font-bold">
                        Main Gate
                      </p>
                      <p
                        className={`text-sm font-bold ${
                          scanResult.isMainEntryScanned
                            ? "text-green-700"
                            : "text-gray-400"
                        }`}
                      >
                        {scanResult.isMainEntryScanned
                          ? "✓ SCANNED"
                          : "○ PENDING"}
                      </p>
                    </div>
                    {scanResult.hasTimeslots && (
                      <div
                        className={`p-2 rounded border text-center ${
                          scanResult.isSecondaryEntryScanned
                            ? "bg-green-50 border-green-200"
                            : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <p className="text-[10px] uppercase text-gray-500 font-bold">
                          Activity/Slot
                        </p>
                        <p
                          className={`text-sm font-bold ${
                            scanResult.isSecondaryEntryScanned
                              ? "text-green-700"
                              : "text-gray-400"
                          }`}
                        >
                          {scanResult.isSecondaryEntryScanned
                            ? "✓ SCANNED"
                            : "○ PENDING"}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {scanResult.hasTimeslots &&
                  (scanResult.eventDetails?.timeslot ||
                    scanResult.timeslot) && (
                    <div className="mb-4 sm:mb-6 bg-indigo-50 border border-indigo-100 rounded-lg p-3">
                      <h4 className="text-sm font-bold text-indigo-800 mb-1 flex items-center">
                        ⏱️ Assigned Activity Slot
                      </h4>
                      <p className="text-sm text-indigo-700">
                        {scanResult.eventDetails?.timeslot?.start ||
                          scanResult.timeslot?.start ||
                          "TBD"}{" "}
                        -{" "}
                        {scanResult.eventDetails?.timeslot?.end ||
                          scanResult.timeslot?.end ||
                          "TBD"}
                      </p>
                    </div>
                  )}
              </div>

              <div className="mb-4 sm:mb-6">
                <h4 className="text-sm sm:text-base font-semibold text-gray-700 mb-2">
                  {activeMode === "WORKSHOP"
                    ? "Workshop Details"
                    : "Event Information"}
                </h4>
                <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600">Event:</span>
                    <span className="font-medium text-right">
                      {scanResult.eventDetails?.name ||
                        scanResult.eventName ||
                        "N/A"}
                    </span>
                  </div>
                  {activeMode === "WORKSHOP" && (
                    <div className="flex justify-between gap-2">
                      <span className="text-gray-600">Activity:</span>
                      <span className="font-bold text-amber-600 text-right">
                        {scanResult.passTitle || "N/A"}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600">Date:</span>
                    <span className="font-medium">
                      {scanResult.eventDetails?.date ||
                        (scanResult.timeslot?.start
                          ? new Date(
                              scanResult.timeslot.start,
                            ).toLocaleDateString()
                          : "TBD")}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600">Venue:</span>
                    <span className="font-medium text-right">
                      {scanResult.eventDetails?.venue || "Workshop Venue"}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm sm:text-base font-semibold text-gray-700 mb-2">
                  Attendee
                </h4>
                <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600">Name:</span>
                    <span className="font-medium">
                      {scanResult.attendeeInfo?.name || "Guest"}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium text-right break-all">
                      {scanResult.attendeeInfo?.email || "N/A"}
                    </span>
                  </div>
                  {scanResult.attendeeInfo?.phoneNumber && (
                    <div className="flex justify-between gap-2">
                      <span className="text-gray-600">Phone:</span>
                      <span className="font-medium text-blue-600">
                        {scanResult.attendeeInfo.phoneNumber}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {scanResult.isValid && (
              <div className="flex flex-col gap-3 sm:gap-4">
                {/* Main Entry Button Logic */}
                {!scanResult.isMainEntryScanned ? (
                  <button
                    onClick={() => handleMarkAsUsed("MAIN")}
                    disabled={loading}
                    className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-bold text-base sm:text-lg shadow-lg"
                  >
                    ✅ Confirm Main Gate Entry
                  </button>
                ) : (
                  <div className="w-full px-4 py-3 bg-gray-200 text-gray-500 rounded-lg font-bold text-center border-2 border-dashed border-gray-300">
                    Main Entry Already Completed
                  </div>
                )}

                {/* Secondary Entry Button Logic */}
                {scanResult.hasTimeslots &&
                  (!scanResult.isSecondaryEntryScanned ? (
                    <button
                      onClick={() => handleMarkAsUsed("SECONDARY")}
                      disabled={loading}
                      className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-bold text-base sm:text-lg shadow-lg"
                    >
                      🎟️ Confirm Activity Slot Entry
                    </button>
                  ) : (
                    <div className="w-full px-4 py-3 bg-gray-200 text-gray-500 rounded-lg font-bold text-center border-2 border-dashed border-gray-300">
                      Activity Entry Already Completed
                    </div>
                  ))}

                {/* Cancel Button remains at bottom */}
                <button
                  onClick={handleMarkAsCancelled}
                  disabled={loading}
                  className="w-full px-4 sm:px-6 py-2.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 disabled:opacity-50 font-bold text-sm"
                >
                  🚫 Cancel Ticket
                </button>
              </div>
            )}
          </div>
        )}
        {scanResult && (
          <button
            onClick={resetScanner}
            className="w-full px-4 sm:px-6 py-2.5 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm sm:text-base shadow"
          >
            Back to Scanner
          </button>
        )}
      </div>

      {/* Modal */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        onConfirm={modal.onConfirm}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
    </div>
  );
};

export default EventTicketScanner;
